import { css, Global } from '@emotion/core';
import {
  AutoComplete,
  Button as AntdButton,
  Dropdown,
  Drawer,
  message,
  Modal,
  Spin,
} from 'antd';
import { MenuProps } from 'antd';
import loadImage from 'blueimp-load-image';
import classNames from 'classnames';
import { useRef, useState } from 'react';
import { FilePond } from 'react-filepond';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';
import { Button, EmptyTip, Icon, List } from '@/components';
import { OutputList } from './OutputList';
import { FileItem } from './FileItem';
import { api, resultTypes } from '@/apis';
import { APIFileSearchItem } from '@/apis/file';
import {
  FILE_NOT_EXIST_REASON,
  FILE_SAFE_STATUS,
  FILE_TYPE,
  IMAGE_COVER,
  PARSE_STATUS,
  PROJECT_PERMISSION,
} from '@/constants';
import { FC, File as MFile, Project, Target } from '@/interfaces';
import { AppState } from '@/store';
import { setFilesState } from '@/store/file/slice';
import { editProject, setCurrentProject } from '@/store/project/slice';
import style from '@/style';
import { sanitizeFilename, toLowerCamelCase } from '@/utils';
import { clickEffect } from '@/utils/style';
import { can } from '@/utils/user';
import { routes } from '@/pages/routes';
import { ListPageSpec } from '@/components/shared/List';
import { FilePondFile } from 'filepond';
import { createDebugLogger } from '@/utils/debug-logger';
import { useAiTranslate } from '@/components/ai';
import { FileMoveModal } from '@/components/file/FileMoveModal';
import { ImportFromURLModal } from '@/components/file/ImportFromURLModal';

/** 文件列表的属性接口 */
interface FileListProps {
  project: Project;
  onChangeTargetClick?: () => void;
  target: Target;
  className?: string;
}

const debugLogger = createDebugLogger('components:project:FileList');
/**
 * 文件列表
 */
export const FileList: FC<FileListProps> = ({
  project,
  target,
  onChangeTargetClick,
  className,
}) => {
  const { formatMessage } = useIntl(); // i18n
  const history = useHistory(); // 路由
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [listMode] = useState<'image' | 'text'>('image');
  const [total, setTotal] = useState(0); // 元素总个数
  const runtimeConfig = useSelector(
    (state: AppState) => state.site.runtimeConfig,
  );
  const uploadAPI = `${runtimeConfig.baseURL}/v1/projects/${project.id}/files`;
  const token = useSelector((state: AppState) => state.user.token);
  const user = useSelector((state: AppState) => state.user);
  const platform = useSelector((state: AppState) => state.site.platform);
  const isMobile = platform === 'mobile';
  // 归档项目：仅可查看图片，禁止移动/打开/上传/社交媒体导入/导出/删除等修改操作
  const archived = !!project.archived;
  const [outputDrawerVisible, setOutputDrawerVisible] = useState(false);
  const [moveModalVisible, setMoveModalVisible] = useState(false);
  const [importModalSource, setImportModalSource] = useState<
    | 'twitter'
    | 'twitter_user'
    | 'bluesky'
    | 'bluesky_user'
    | 'pixiv'
    | 'pixiv_user'
    | 'external'
    | null
  >(null);
  const coverWidth = IMAGE_COVER.WIDTH;
  const coverHeight = IMAGE_COVER.HEIGHT;

  const [items, setItems] = useState<MFile[]>([]);
  const [spinningIDs, setSpinningIDs] = useState<string[]>([]); // 删除请求中
  const filePondRef = useRef<FilePond | null>();
  const currentPageSpecRef = useRef<ListPageSpec | null>(null);

  const defaultPage = useSelector(
    (state: AppState) => state.file.filesState.page,
  );
  const defaultWord = useSelector(
    (state: AppState) => state.file.filesState.word,
  );
  const defaultScrollTop = useSelector(
    (state: AppState) => state.file.filesState.scrollTop,
  );
  const selectedFileIds = useSelector(
    (state: AppState) => state.file.filesState.selectedFileIds,
  );
  const [aiEnabled, aiTranslateApi, aiModalHolder] = useAiTranslate(
    [...new Set(selectedFileIds)]
      .map((id) => items.find((item) => item.id === id))
      .filter(Boolean) as MFile[],
    target,
  );

  // ===== 跨组（团队）文件搜索 =====
  const [searchWord, setSearchWord] = useState('');
  const [searchResults, setSearchResults] = useState<APIFileSearchItem[]>([]);

  const handleFileSearch = (word: string) => {
    const w = word.trim();
    if (!w) {
      setSearchResults([]);
      return;
    }
    api.file
      .searchFiles({ word: w, limit: 20 })
      .then((result) => {
        setSearchResults(result.data || []);
      })
      .catch(() => {
        setSearchResults([]);
      });
  };
  const debouncedFileSearch = useDebouncedCallback(handleFileSearch, 300);

  const searchOptions: any[] = searchResults.map((item) => ({
    value: item.name,
    disabled: !item.can_access,
    __item: item as APIFileSearchItem,
    label: (
      <div className="FileSearch__Item">
        <div className="FileSearch__Name">{item.name}</div>
        <div className="FileSearch__Path">
          {item.team_name} &gt; {item.project_set_name} &gt; {item.project_name}
        </div>
        {!item.can_access && (
          <span className="FileSearch__Tag">
            {formatMessage({ id: 'file.searchNotJoined' })}
          </span>
        )}
      </div>
    ),
  }));

  const onSelectFile = (_value: string, option: any) => {
    const item = option && option.__item ? option.__item : null;
    if (!item || !item.can_access) {
      return;
    }
    history.push(
      routes.dashboard.project.show.replace(':projectId', item.project_id),
    );
    setSearchWord('');
    setSearchResults([]);
  };

  const openInTranslator = (file: MFile) => {
    history.push(routes.imageTranslator.build(file.id, target.id));
  };

  const deleteFile = (file: MFile) => {
    Modal.confirm({
      title: formatMessage({ id: 'project.deleteFile' }),
      content: formatMessage(
        { id: 'project.deleteFileTip' },
        { name: file.name },
      ),
      onOk: () => {
        setSpinningIDs((ids) => [file.id, ...ids]);
        api.file
          .deleteFile({
            id: file.id,
          })
          .then((result) => {
            message.success(result.data.message);
            // 修改数据
            setItems((items) => {
              return items.filter((item) => item.id !== file.id);
            });
          })
          .catch((error) => {
            error.default();
          })
          .finally(() => {
            setSpinningIDs((ids) => ids.filter((id) => id !== file.id));
          });
      },
      onCancel: () => {},
      okText: formatMessage({ id: 'form.ok' }),
      cancelText: formatMessage({ id: 'form.cancel' }),
    });
  };

  /** 处理文件添加 */
  const handleFileAdd = (error: any, file: FilePondFile) => {
    // 加载预览图
    const realFile = file.file;
    if (/^image.+/.test(file.file.type)) {
      loadImage(
        realFile,
        (img) => {
          setItems((items) =>
            items.map((item) => {
              if (item.id === file.id && img) {
                return {
                  ...item,
                  coverUrl: (img as HTMLCanvasElement).toDataURL(),
                };
              }
              return item;
            }),
          );
        },
        {
          maxWidth: coverWidth,
          maxHeight: coverHeight,
          crop: true,
          canvas: true,
          ...({ imageSmoothingQuality: 'high' } as any), // @type 版本太旧
        },
      );
    }
    const uploadingFile: MFile = {
      // this is the random id generated by filepond
      id: file.id,
      name: file.filename,
      saveName: '',
      type: FILE_TYPE.IMAGE,
      sourceCount: 0,
      translatedSourceCount: 0,
      checkedSourceCount: 0,
      fileNotExistReason: FILE_NOT_EXIST_REASON.UNKNOWN,
      safeStatus: FILE_SAFE_STATUS.NEED_MACHINE_CHECK,
      parseStatus: PARSE_STATUS.NOT_START,
      parseStatusDetailName: formatMessage({ id: 'file.parseNotStart' }),
      parseErrorTypeDetailName: '',
      url: '',
      parentID: null,
      fileTargetCache: {
        translatedSourceCount: 0,
        checkedSourceCount: 0,
      },
      uploading: true,
      uploadState: 'uploading',
      uploadPercent: 0,
    };
    setItems((items) => [uploadingFile, ...items]);
  };

  /** 获取元素 */
  const loadPage = ({ page, pageSize, word, cancelToken }: ListPageSpec) => {
    setLoading(true);
    currentPageSpecRef.current = {
      page,
      pageSize,
      word: word || '',
      cancelToken,
    };
    return api.file
      .getProjectFiles({
        projectID: project.id,
        params: {
          page,
          limit: pageSize,
          word,
          target: target.id,
        },
        configs: {
          cancelToken,
        },
      })
      .then((result) => {
        const data = (result.data as MFile[]).map((d) => toLowerCamelCase(d));
        setItems(data);
        setTotal(Number(result.headers['x-pagination-count']));
        setLoading(false);
      })
      .catch((error) => {
        // 如果是 cancel 的请求，则不取消 loading 状态，因为肯定有下一个请求
        if (error.type !== resultTypes.CANCEL_FAILURE) {
          setLoading(false);
        }
        error.default();
      });
  };
  /*

  const startOCR = () => {
    Modal.confirm({
      title: formatMessage({ id: 'project.startOCR' }),
      content: formatMessage({ id: 'project.startOCRTip' }),
      onOk: () => {
        apis
          .startProjectOCR({ id: project.id })
          .then((result) => {
            // TODO 自动刷新实现后删除此备注
            message.success(
              result.data.message + '（请稍后刷新页面查看进度）',
              2
            );
          })
          .catch((error) => {
            error?.default();
          });
      },
      onCancel: () => {},
      okText: formatMessage({ id: 'form.ok' }),
      cancelText: formatMessage({ id: 'form.cancel' }),
    });
  };
  // */

  debugLogger('items', items);

  // ===== 移动端“更多”菜单 =====
  // 顺序（设计裁决）：移动图片 → AI辅助翻译 → 导出 → 上传 → 分割线 → 社媒导入分组。
  // 切换目标语言常驻顶栏，不进菜单；权限/归档/AI 条件与桌面按钮完全一致。
  const mobileMenuItems: MenuProps['items'] = [];
  if (!archived && can(project, PROJECT_PERMISSION.MOVE_FILE)) {
    mobileMenuItems.push({
      key: 'move',
      label:
        formatMessage({ id: 'file.moveImages' }) +
        (selectedFileIds.length > 0 ? ` (${selectedFileIds.length})` : ''),
      disabled: selectedFileIds.length === 0,
      onClick: () => setMoveModalVisible(true),
    });
  }
  if (!archived && aiEnabled && aiTranslateApi) {
    mobileMenuItems.push({
      key: 'ai-translate',
      label: formatMessage({ id: 'fileList.aiTranslate.buttonText' }),
      onClick: () =>
        aiTranslateApi.start({
          onFileSaved(f2) {
            setItems((items) => items.map((f) => (f.id === f2.id ? f2 : f)));
          },
        }),
    });
  }
  if (!archived && can(project, PROJECT_PERMISSION.OUTPUT_TRA)) {
    mobileMenuItems.push({
      key: 'export',
      label:
        formatMessage({ id: 'project.export' }) +
        (selectedFileIds.length > 0 ? ` (${selectedFileIds.length})` : ''),
      onClick: () => setOutputDrawerVisible(true),
    });
  }
  if (!archived && can(project, PROJECT_PERMISSION.ADD_FILE)) {
    mobileMenuItems.push({
      key: 'upload',
      label: formatMessage({ id: 'site.upload' }),
      onClick: () => {
        filePondRef.current?.browse();
      },
    });
    mobileMenuItems.push({ type: 'divider', key: 'social-divider' });
    mobileMenuItems.push({
      key: 'social-import',
      type: 'group',
      label: formatMessage({ id: 'file.importFromSocial' }),
      children: [
        {
          key: 'twitter',
          label: formatMessage({ id: 'file.importFromTwitter' }),
          onClick: () => setImportModalSource('twitter'),
        },
        {
          key: 'twitter_user',
          label: formatMessage({ id: 'file.importFromTwitterUser' }),
          onClick: () => setImportModalSource('twitter_user'),
        },
        {
          key: 'bluesky',
          label: formatMessage({ id: 'file.importFromBluesky' }),
          onClick: () => setImportModalSource('bluesky'),
        },
        {
          key: 'bluesky_user',
          label: formatMessage({ id: 'file.importFromBlueskyUser' }),
          onClick: () => setImportModalSource('bluesky_user'),
        },
        {
          key: 'pixiv',
          label: formatMessage({ id: 'file.importFromPixiv' }),
          onClick: () => setImportModalSource('pixiv'),
        },
        {
          key: 'pixiv_user',
          label: formatMessage({ id: 'file.importFromPixivUser' }),
          onClick: () => setImportModalSource('pixiv_user'),
        },
        {
          key: 'external',
          label: formatMessage({ id: 'file.importFromExternal' }),
          onClick: () => setImportModalSource('external'),
        },
      ],
    });
  }
  // 尾随分割线（无社媒分组时上传是最后一项）不应保留
  const lastItem = mobileMenuItems[mobileMenuItems.length - 1];
  if (lastItem && 'type' in lastItem && lastItem.type === 'divider') {
    mobileMenuItems.pop();
  }

  return (
    <div
      className={classNames('FileList', className, {
        'FileList--image': listMode === 'image',
      })}
      css={css`
        flex: auto;
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        align-items: stretch;
        .FileList__Header {
          width: 100%;
          height: 45px;
          flex: none;
        }
        .FileList__SubHeader {
        }
        .FileList__List {
          .List__Items {
            padding: 0 ${style.paddingBase}px;
            .List__ItemWrapper {
              flex: none;
              margin-bottom: 10px;
            }
          }
        }
        .FileList__FileItem {
          margin: 0 auto;
        }
        .FileList__Header {
          display: flex;
          justify-content: stretch;
        }
        .FileList__ChangeTargetButton {
          flex: auto;
          .Button__Content {
            justify-content: flex-start;
          }
        }
      `}
    >
      <Global
        styles={css`
          .ant-drawer-body {
            padding: 0 !important;
          }
          /* ===== 移动端 Header（设计裁决）：常驻切换目标语言药丸 + 右对齐更多按钮 ===== */
          .FileList__Header--mobile {
            display: flex;
            justify-content: flex-end;
            align-items: center;
            gap: 8px;
            padding: 0 8px;
          }
          .FileList__ChangeTargetPill {
            flex: 1 1 auto;
            min-width: 0;
            height: 34px;
            padding: 0 12px;
            display: flex;
            align-items: center;
            gap: 6px;
            background-color: var(--moeflow-surface);
            border: 1px solid var(--moeflow-borderColorBase);
            border-radius: 17px;
            color: ${style.textColor};
            font-size: 13px;
            cursor: pointer;
            ${clickEffect()};
            .FileList__ChangeTargetPillIcon,
            .FileList__ChangeTargetPillCaret {
              flex: none;
              color: ${style.textColorSecondary};
            }
            .FileList__ChangeTargetPillText {
              flex: 1 1 auto;
              min-width: 0;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
              text-align: left;
            }
          }
          .FileList__MoreButton {
            flex: none;
            width: 44px;
            height: 44px;
            padding: 5px;
            display: flex;
            align-items: center;
            justify-content: center;
            background-color: transparent;
            border: none;
            cursor: pointer;
            > span {
              width: 34px;
              height: 34px;
              display: inline-flex;
              align-items: center;
              justify-content: center;
              background-color: var(--moeflow-surface);
              border: 1px solid var(--moeflow-borderColorLight);
              border-radius: 8px;
              color: ${style.textColor};
              font-size: 16px;
            }
            &:active > span {
              background-color: var(--moeflow-activeColor);
            }
          }
          /* 浮层菜单（portal 到 body）：唯一 overlayClassName，仅作用本组件 */
          .FileList__MoreDropdown {
            .ant-dropdown-menu {
              max-height: 70vh;
              overflow-y: auto;
              -webkit-overflow-scrolling: touch;
              overscroll-behavior: contain;
            }
          }
          .FileList__Search {
            flex: none;
            width: 100%;
            padding: 0 ${style.paddingBase}px;
            margin-top: ${style.paddingBase}px;
            margin-bottom: ${style.paddingBase}px;
            .ant-select-selector,
            .ant-input-affix-wrapper {
              border-radius: ${style.borderRadiusBase} !important;
            }
          }
          .FileSearch__Item {
            display: flex;
            flex-direction: column;
            .FileSearch__Name {
              font-size: 14px;
              color: ${style.textColor};
              font-weight: bold;
            }
            .FileSearch__Path {
              font-size: 12px;
              color: ${style.textColorSecondary};
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }
            .FileSearch__Tag {
              align-self: flex-start;
              margin-top: 2px;
              font-size: 12px;
              color: ${style.textColorSecondary};
              background-color: ${style.backgroundColorLight};
              border-radius: ${style.borderRadiusBase};
              padding: 0 6px;
            }
          }
        `}
      />
      <FilePond
        name="file"
        className="FileList__FilePond"
        ref={(ref) => (filePondRef.current = ref)}
        css={css`
          display: none;
        `}
        dropOnPage
        dropOnElement={false}
        allowMultiple
        maxParallelUploads={5}
        onaddfile={handleFileAdd}
        // 上传中
        onprocessfileprogress={(file, progress) => {
          setItems((items) =>
            items.map((item) => {
              if (item.id === file.id) {
                return {
                  ...item,
                  uploadPercent: Math.floor(progress * 100),
                };
              }
              return item;
            }),
          );
        }}
        // 上传成功
        onprocessfile={(error, file) => {
          if (error) return;
          const result = toLowerCamelCase(JSON.parse(file.serverId) as MFile);
          setItems((items) => {
            // 覆盖时删除列表中原来的文件
            const itemsWithoutSameID = items.filter(
              (item) => item.id !== result.id,
            );
            const updated = itemsWithoutSameID.map((item) => {
              if (item.id === file.id) {
                // return a new MFile item , overwriting its (Filepond-created) id
                return {
                  ...item,
                  ...result,
                  uploadState: 'success',
                } as MFile;
              }
              return item;
            });
            debugLogger('updated on upload success', updated);
            return updated;
          });
        }}
        // 上传失败
        onerror={(error, file) => {
          if (!file || !file.id) {
            return;
          }
          // 识别重复图片（后端 code=8008），提示已有位置并移除占位卡片
          let isDuplicate = false;
          try {
            const body =
              typeof error.body === 'string' ? JSON.parse(error.body) : error.body;
            if (body && body.code === 8008) {
              isDuplicate = true;
              message.warning(body.message, 4);
              setItems((items) => items.filter((item) => item.id !== file.id));
            }
          } catch (e) {
            // 非 JSON 响应体，忽略
          }
          if (!isDuplicate) {
            setItems((items) =>
              items.map((item) => {
                if (item.id === file.id) {
                  return {
                    ...item,
                    uploadState: 'failure',
                  };
                }
                return item;
              }),
            );
          }
        }}
        server={{
          process: {
            url: uploadAPI,
            headers: { Authorization: `Bearer ${token}` },
            // 上传前去掉文件名中的 emoji（后端以 multipart 文件名为入库名）
            ondata: (fd) => {
              const file = fd.get('file');
              if (file instanceof File) {
                const cleanName = sanitizeFilename(file.name);
                if (cleanName && cleanName !== file.name) {
                  fd.set(
                    'file',
                    new File([file], cleanName, { type: file.type }),
                  );
                }
              }
              return fd;
            },
          },
        }}
      />
      <div
        className={classNames('FileList__Header', {
          'FileList__Header--mobile': isMobile,
        })}
      >
        {isMobile ? (
          <>
            {/* 切换目标语言：常驻顶栏（不进菜单） */}
            <button
              type="button"
              className="FileList__ChangeTargetPill"
              aria-label={
                formatMessage({ id: 'project.changeTarget' }) +
                ' - ' +
                target?.language.i18nName
              }
              onClick={onChangeTargetClick}
            >
              <Icon className="FileList__ChangeTargetPillIcon" icon="exchange-alt" />
              <span className="FileList__ChangeTargetPillText">
                {target?.language.i18nName}
              </span>
              <Icon className="FileList__ChangeTargetPillCaret" icon="caret-down" />
            </button>
            <Dropdown
              trigger={['click']}
              placement="bottomRight"
              overlayClassName="FileList__MoreDropdown"
              menu={{ items: mobileMenuItems }}
            >
              <button
                type="button"
                className="FileList__MoreButton"
                aria-label="更多"
              >
                <Icon icon="ellipsis-h" />
              </button>
            </Dropdown>
          </>
        ) : (
          <>
            {!archived && can(project, PROJECT_PERMISSION.MOVE_FILE) && (
          <Button
            icon="arrows-alt"
            disabled={selectedFileIds.length === 0}
            onClick={() => setMoveModalVisible(true)}
          >
            {formatMessage({ id: 'file.moveImages' })}
            {selectedFileIds.length > 0 && ` (${selectedFileIds.length})`}
          </Button>
        )}
        <Button
          className="FileList__ChangeTargetButton"
          icon="exchange-alt"
          iconProps={{
            style: { height: '16px', width: '16px' },
          }}
          onClick={onChangeTargetClick}
        >
          {(!isMobile
            ? formatMessage({ id: 'project.changeTarget' }) + ' - '
            : '') + target?.language.i18nName}
        </Button>
        {!archived && aiEnabled && aiTranslateApi && (
          <Button
            tooltipProps={{
              overlay: formatMessage({ id: 'fileList.aiTranslate.buttonTip' }),
            }}
            icon="robot"
            onClick={() =>
              aiTranslateApi.start({
                onFileSaved(f2) {
                  setItems((items) =>
                    items.map((f) => (f.id === f2.id ? f2 : f)),
                  );
                },
              })
            }
          >
            {formatMessage({ id: 'fileList.aiTranslate.buttonText' })}
          </Button>
        )}
        {/* {can(team, TEAM_PERMISSION.USE_OCR_QUOTA) && (
        )} */}
        {/* <Button
          tooltipProps={{
            overlay: formatMessage({ id: 'fileList.changeMode' }),
          }}
          icon={listMode === 'text' ? 'th-large' : 'th-list'}
          onClick={() => {
            setListMode(listMode === 'text' ? 'image' : 'text');
          }}
        ></Button> */}
        {!archived && can(project, PROJECT_PERMISSION.OUTPUT_TRA) && (
          <Button icon="download" onClick={() => setOutputDrawerVisible(true)}>
            {!isMobile && formatMessage({ id: 'project.export' })}
            {selectedFileIds.length > 0 && ` (${selectedFileIds.length})`}
          </Button>
        )}
        {!archived && can(project, PROJECT_PERMISSION.ADD_FILE) && (
          <Button
            icon="plus"
            onClick={() => {
              filePondRef.current?.browse();
            }}
          >
            {!isMobile && formatMessage({ id: 'site.upload' })}
          </Button>
        )}
        {!archived && can(project, PROJECT_PERMISSION.ADD_FILE) && (
          <Dropdown
            menu={
              {
                items: [
                  {
                    key: 'twitter',
                    label: formatMessage({ id: 'file.importFromTwitter' }),
                    onClick: () => setImportModalSource('twitter'),
                  },
                  {
                    key: 'twitter_user',
                    label: formatMessage({ id: 'file.importFromTwitterUser' }),
                    onClick: () => setImportModalSource('twitter_user'),
                  },
                  {
                    key: 'bluesky',
                    label: formatMessage({ id: 'file.importFromBluesky' }),
                    onClick: () => setImportModalSource('bluesky'),
                  },
                  {
                    key: 'bluesky_user',
                    label: formatMessage({ id: 'file.importFromBlueskyUser' }),
                    onClick: () => setImportModalSource('bluesky_user'),
                  },
                  {
                    key: 'pixiv',
                    label: formatMessage({ id: 'file.importFromPixiv' }),
                    onClick: () => setImportModalSource('pixiv'),
                  },
                  {
                    key: 'pixiv_user',
                    label: formatMessage({ id: 'file.importFromPixivUser' }),
                    onClick: () => setImportModalSource('pixiv_user'),
                  },
                  {
                    key: 'external',
                    label: formatMessage({ id: 'file.importFromExternal' }),
                    onClick: () => setImportModalSource('external'),
                  },
                ],
              } as MenuProps
            }
          >
            <Button icon="download">
              {!isMobile && formatMessage({ id: 'file.importFromSocial' })}
            </Button>
          </Dropdown>
        )}
          </>
        )}
      </div>
      <div className="FileList__Search">
        <AutoComplete
          value={searchWord}
          options={searchOptions}
          style={{ width: '100%' }}
          onSearch={(value) => {
            setSearchWord(value);
            debouncedFileSearch(value);
          }}
          onChange={(value) => {
            setSearchWord(value);
            if (!value) {
              setSearchResults([]);
            }
          }}
          onSelect={onSelectFile}
          filterOption={false}
          placeholder={formatMessage({ id: 'file.searchPlaceholder' })}
          notFoundContent={
            searchWord.trim()
              ? formatMessage({ id: 'file.searchEmpty' })
              : null
          }
        />
      </div>
      {selectedFileIds.length > 0 && (
        <div className="FileList__SubHeader">
          <AntdButton
            type="link"
            onClick={() => {
              const idsOnThisPage = items.map((item) => item.id);
              const selectedIdsOnOtherPages = selectedFileIds.filter(
                (id) => !idsOnThisPage.includes(id),
              );
              dispatch(
                setFilesState({
                  selectedFileIds: [
                    ...selectedIdsOnOtherPages,
                    ...idsOnThisPage,
                  ],
                }),
              );
            }}
          >
            {formatMessage({ id: 'site.selectPageAll' })}
          </AntdButton>
          <AntdButton
            type="link"
            onClick={() => {
              const idsOnThisPage = items.map((item) => item.id);
              const selectedIdsOnThisPage = selectedFileIds.filter((id) =>
                idsOnThisPage.includes(id),
              );
              const selectedIdsOnOtherPages = selectedFileIds.filter(
                (id) => !idsOnThisPage.includes(id),
              );
              const invertIds = idsOnThisPage.filter(
                (id) => !selectedIdsOnThisPage.includes(id),
              );
              dispatch(
                setFilesState({
                  selectedFileIds: [...selectedIdsOnOtherPages, ...invertIds],
                }),
              );
            }}
          >
            {formatMessage({ id: 'site.selectPageInverse' })}
          </AntdButton>
          <AntdButton
            type="link"
            onClick={() => {
              dispatch(setFilesState({ selectedFileIds: [] }));
            }}
          >
            {formatMessage({ id: 'site.selectCancel' })}
          </AntdButton>
        </div>
      )}
      <List
        id={project.id}
        className="FileList__List"
        searchInputVisible={false}
        onChange={loadPage}
        loading={loading}
        total={total}
        items={items}
        itemHeight={296}
        multiColumn
        columnWidth={200}
        paginationProps={{
          disabled: spinningIDs.length > 0,
        }}
        refetchOnPageSizeChange
        itemCreater={(file) => {
          return (
            <Spin spinning={spinningIDs.indexOf(file.id) > -1}>
              <FileItem
                className="FileList__FileItem"
                file={file}
                projectID={project.id}
                roleSystemCode={project.role?.systemCode}
                isAdmin={user.admin}
                hasTarget={Boolean(target)}
                withProofread={project.withProofread !== false}
                withAuthor={project.withAuthor === true}
                renameEnabled={
                  !archived &&
                  can(project, PROJECT_PERMISSION.RENAME_FILE) &&
                  (file.uploadState === undefined || file.uploadState === 'success')
                }
                onFileChanged={(patch) => {
                  setItems((prev) =>
                    prev.map((item) =>
                      item.id === patch.id
                        ? { ...item, ...patch }
                        : item,
                    ),
                  );
                }}
                onClick={() => {
                  if (archived) {
                    return;
                  }
                  (file.uploadState === undefined ||
                    file.uploadState === 'success') &&
                    openInTranslator(file);
                }}
                selectVisible={
                  !archived && can(project, PROJECT_PERMISSION.OUTPUT_TRA)
                }
                selected={selectedFileIds.includes(file.id)}
                onSelect={(value) => {
                  if (value) {
                    dispatch(
                      setFilesState({
                        selectedFileIds: [...selectedFileIds, file.id],
                      }),
                    );
                  } else {
                    dispatch(
                      setFilesState({
                        selectedFileIds: selectedFileIds.filter(
                          (id) => id !== file.id,
                        ),
                      }),
                    );
                  }
                }}
                deleteButtonVisible={
                  !archived &&
                  can(project, PROJECT_PERMISSION.DELETE_FILE) &&
                  (file.uploadState === undefined ||
                    file.uploadState === 'success')
                }
                onDeleteButtonClick={() => {
                  deleteFile(file);
                }}
              />
            </Spin>
          );
        }}
        emptyTipCreater={() => {
          return (
            <EmptyTip
              className="ProjectList__EmptyTip"
              text={
                <>
                  <p>{formatMessage({ id: 'file.uploadTip1' })}</p>
                  <p>{formatMessage({ id: 'file.uploadTip2' })}</p>
                </>
              }
            />
          );
        }}
        searchEmptyTipCreater={(word) => {
          return (
            <EmptyTip
              className="ProjectList__EmptyTip"
              text={formatMessage({ id: 'file.emptySearchTip' }, { word })}
            />
          );
        }}
        defaultPage={defaultPage}
        onPageChange={(page) => {
          dispatch(setFilesState({ page }));
        }}
        defaultWord={defaultWord}
        onWordChange={(word) => {
          dispatch(setFilesState({ word }));
        }}
        defaultScrollTop={defaultScrollTop}
        onScrollTopChange={(scrollTop) => {
          dispatch(setFilesState({ scrollTop }));
        }}
      />
      <Drawer
        className="FileList__OutputDrawer"
        title={
          formatMessage({ id: 'project.export' }) +
          ` - ${target.language.i18nName}`
        }
        placement={isMobile ? 'bottom' : 'right'}
        onClose={() => setOutputDrawerVisible(false)}
        open={outputDrawerVisible}
        width={350}
        height="70%"
        destroyOnClose={true}
      >
        <OutputList
          projectID={project.id}
          targetID={target.id}
          selectedFileIds={selectedFileIds}
          onExported={() => {
            if (currentPageSpecRef.current) loadPage(currentPageSpecRef.current);
            // 导出成功后刷新项目数据（has_output 等），使进度条绿条立即更新
            api.project
              .getProject({ id: project.id })
              .then((res) => {
                const data = toLowerCamelCase(res.data);
                dispatch(editProject(data));
                dispatch(setCurrentProject(data));
              })
              .catch(() => {});
          }}
        />
      </Drawer>
      {aiModalHolder}
      <FileMoveModal
        open={moveModalVisible}
        onClose={() => setMoveModalVisible(false)}
        projectID={project.id}
        files={[...new Set(selectedFileIds)]
          .map((id) => items.find((item) => item.id === id))
          .filter(Boolean) as MFile[]}
        onSaved={(movedIds) => {
          // 移动成功后从当前列表移除已移动的图片（失败的保留）
          setItems((prev) => prev.filter((it) => !movedIds.includes(it.id)));
        }}
      />
      <ImportFromURLModal
        open={importModalSource !== null}
        source={importModalSource ?? 'external'}
        onClose={() => setImportModalSource(null)}
        projectID={project.id}
        onSaved={() => {
          // 下载导入成功后重新拉取列表（跳回第一页）：
          // 不手动构造 items 插入，避免后端返回字段与列表渲染所需的
          // 完整结构不一致时崩溃白屏，改为走正常列表接口刷新。
          const pageSpec = currentPageSpecRef.current;
          if (pageSpec) loadPage({ ...pageSpec, page: 1 });
        }}
      />
    </div>
  );
};
